import React, { useState, useMemo } from 'react'
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
} from 'recharts'
import {
  ChartContainer,
  ChartTooltip,
  ChartLegend,
} from '@/components/ui/chart'
import { format } from 'date-fns'
import dayjs from 'dayjs'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import useSWR from 'swr'
import { fetcher } from '@/hooks'
import Skeleton from '@/components/ui/skeleton'
import { Maximize, Minimize } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useContrastModeStore } from '@/components/roi/ContrastModeSelector'
import { getContrastDate } from '@/lib/contrast-date-utils'
import { getWeek } from '@/lib/date-utils'

const dimensionOptions = [
  { value: 'none', label: 'None' },
  { value: 'ad_unit', label: 'Ad Unit' },
  { value: 'app_version', label: 'Version' },
  { value: 'format', label: 'Format' },
]

function AdmobChart({
  title,
  chartId,
  metricLabels,
  baseMetric,
  baseQueryParams,
  contrastQueryParams,
  isEnlarged,
  onToggleEnlarge,
}) {
  const [splitBy, setSplitBy] = useState('none')
  const [hiddenKeys, setHiddenKeys] = useState([])

  // 获取对比模式状态
  const contrastEnabled = useContrastModeStore((state) => state.enabled)
  const contrastType = useContrastModeStore((state) => state.type)
  const customYear = useContrastModeStore((state) => state.customYear)
  const customMonth = useContrastModeStore((state) => state.customMonth)

  const chartQueryParams = useMemo(() => {
    const params = { ...baseQueryParams }
    const dimensions = new Set(params.dimensions.split(','))

    if (splitBy !== 'none') {
      dimensions.add(splitBy)
    }

    params.dimensions = Array.from(dimensions).join(',')
    return params
  }, [baseQueryParams, splitBy])

  // 对比数据查询参数
  const contrastChartQueryParams = useMemo(() => {
    if (!contrastQueryParams) return null
    const params = { ...contrastQueryParams }
    const dimensions = new Set(params.dimensions.split(','))

    if (splitBy !== 'none') {
      dimensions.add(splitBy)
    }

    params.dimensions = Array.from(dimensions).join(',')
    return params
  }, [contrastQueryParams, splitBy])

  const { data, error, isLoading } = useSWR(
    ['/admob/report', chartQueryParams],
    fetcher,
  )

  // 查询对比数据
  const { data: contrastData } = useSWR(
    contrastEnabled && contrastChartQueryParams
      ? ['/admob/report', contrastChartQueryParams]
      : null,
    fetcher,
  )

  // 日期格式转换工具函数
  const formatDateForContrast = (dateStr) => {
    return parseInt(dateStr.replace(/-/g, ''), 10)
  }

  const parseContrastDate = (dateNum) => {
    const dateStr = String(dateNum)
    return `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}-${dateStr.slice(6, 8)}`
  }

  const buildDateList = (start, end) => {
    if (!start || !end) return []
    const startObj = dayjs(String(start), 'YYYYMMDD')
    const endObj = dayjs(String(end), 'YYYYMMDD')
    if (!startObj.isValid() || !endObj.isValid()) return []
    const result = []
    let cursor = startObj.startOf('day')
    const last = endObj.startOf('day')
    while (!cursor.isAfter(last)) {
      result.push(cursor.format('YYYY-MM-DD'))
      cursor = cursor.add(1, 'day')
    }
    return result
  }

  const processedData = useMemo(() => {
    if (!data) return { chartData: [], yAxisKeys: [], contrastChartData: [] }

    const dateList = buildDateList(
      baseQueryParams?.startDate,
      baseQueryParams?.endDate,
    )
    const sortedData = [...data].sort(
      (a, b) => new Date(a.date) - new Date(b.date),
    )

    // 处理对比数据 - 按对比日期建立索引
    let contrastDataMap = {}
    if (contrastEnabled && contrastData) {
      const sortedContrastData = [...contrastData].sort(
        (a, b) => new Date(a.date) - new Date(b.date),
      )

      sortedContrastData.forEach((row) => {
        const contrastDateStr = row.date
        if (!contrastDataMap[contrastDateStr]) {
          contrastDataMap[contrastDateStr] = {}
        }

        if (splitBy === 'none') {
          contrastDataMap[contrastDateStr][`${baseMetric}_contrast`] =
            parseFloat(row[baseMetric]) || 0
        } else {
          const splitValue = row[splitBy]
          if (splitValue) {
            if (!contrastDataMap[contrastDateStr][splitValue]) {
              contrastDataMap[contrastDateStr][splitValue] = {}
            }
            contrastDataMap[contrastDateStr][`${splitValue}_contrast`] =
              parseFloat(row[baseMetric]) || 0
          }
        }
      })
    }

    if (splitBy === 'none') {
      const dataByDate = {}
      sortedData.forEach((row) => {
        const result = {
          ...row,
          [baseMetric]: parseFloat(row[baseMetric]) || 0,
        }
        // 添加对比数据 - 从当前日期计算对比日期，然后在对比数据中查找
        if (contrastEnabled) {
          const currentDateStr = row.date
          const currentDateNum = formatDateForContrast(currentDateStr)
          const contrastDateNum = getContrastDate(
            currentDateNum,
            contrastType,
            customYear,
            customMonth,
          )
          if (contrastDateNum) {
            const contrastDateStr = parseContrastDate(contrastDateNum)
            if (contrastDataMap[contrastDateStr]) {
              result[`${baseMetric}_contrast`] =
                contrastDataMap[contrastDateStr][`${baseMetric}_contrast`] || null
            }
          }
        }
        dataByDate[row.date] = result
      })

      if (contrastEnabled) {
        dateList.forEach((dateStr) => {
          if (dataByDate[dateStr]) return
          const currentDateNum = formatDateForContrast(dateStr)
          const contrastDateNum = getContrastDate(
            currentDateNum,
            contrastType,
            customYear,
            customMonth,
          )
          if (!contrastDateNum) return
          const contrastDateStr = parseContrastDate(contrastDateNum)
          const contrastEntry = contrastDataMap[contrastDateStr]
          if (!contrastEntry) return
          dataByDate[dateStr] = {
            date: dateStr,
            [baseMetric]: null,
            [`${baseMetric}_contrast`]:
              contrastEntry[`${baseMetric}_contrast`] || null,
          }
        })
      }

      const dataWithParsedMetrics = Object.values(dataByDate).sort(
        (a, b) => new Date(a.date) - new Date(b.date),
      )
      const yAxisKeys = [baseMetric]
      if (contrastEnabled) {
        yAxisKeys.push(`${baseMetric}_contrast`)
      }
      return {
        chartData: dataWithParsedMetrics,
        yAxisKeys,
        contrastChartData: [],
      }
    }

    const dataByDate = {}
    const yAxisKeys = new Set()

    sortedData.forEach((row) => {
      const date = row.date
      if (!dataByDate[date]) {
        dataByDate[date] = { date }
      }
      const splitValue = row[splitBy]
      if (splitValue) {
        dataByDate[date][splitValue] = parseFloat(row[baseMetric]) || 0
        yAxisKeys.add(splitValue)
        // 添加对比数据 - 从当前日期计算对比日期，然后在对比数据中查找
        if (contrastEnabled) {
          const currentDateNum = formatDateForContrast(date)
          const contrastDateNum = getContrastDate(
            currentDateNum,
            contrastType,
            customYear,
            customMonth,
          )
          if (contrastDateNum) {
            const contrastDateStr = parseContrastDate(contrastDateNum)
            if (contrastDataMap[contrastDateStr] && contrastDataMap[contrastDateStr][`${splitValue}_contrast`] != null) {
              dataByDate[date][`${splitValue}_contrast`] =
                contrastDataMap[contrastDateStr][`${splitValue}_contrast`]
              yAxisKeys.add(`${splitValue}_contrast`)
            }
          }
        }
      }
    })

    if (contrastEnabled) {
      dateList.forEach((dateStr) => {
        const currentDateNum = formatDateForContrast(dateStr)
        const contrastDateNum = getContrastDate(
          currentDateNum,
          contrastType,
          customYear,
          customMonth,
        )
        if (!contrastDateNum) return
        const contrastDateStr = parseContrastDate(contrastDateNum)
        const contrastEntry = contrastDataMap[contrastDateStr]
        if (!contrastEntry) return
        if (!dataByDate[dateStr]) {
          dataByDate[dateStr] = { date: dateStr }
        }
        Object.entries(contrastEntry).forEach(([key, value]) => {
          if (!key.endsWith('_contrast')) return
          if (dataByDate[dateStr][key] == null) {
            dataByDate[dateStr][key] = value
          }
          yAxisKeys.add(key)
        })
      })
    }

    return {
      chartData: Object.values(dataByDate).sort(
        (a, b) => new Date(a.date) - new Date(b.date),
      ),
      yAxisKeys: Array.from(yAxisKeys),
      contrastChartData: [],
    }
  }, [
    data,
    contrastData,
    splitBy,
    baseMetric,
    contrastEnabled,
    contrastType,
    customYear,
    customMonth,
    baseQueryParams,
  ])

  const { chartData, yAxisKeys } = processedData

  const formatAxis = (value) => {
    if (value === null || value === undefined) return 'N/A'
    const num = parseFloat(value)
    if (isNaN(num)) return value

    if (
      ['estimated_earnings', 'observed_ecpm'].includes(baseMetric) &&
      !isNaN(num)
    ) {
      const formatted = num.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
      return num < 1000 ? `$${formatted}` : `$${(num / 1000).toFixed(1)}k`
    }

    if (
      ['ad_requests', 'matched_requests', 'impressions', 'clicks'].includes(
        baseMetric,
      )
    ) {
      return num < 1000 ? num.toLocaleString() : `${(num / 1000).toFixed(1)}k`
    }

    if (['match_rate', 'show_rate', 'impression_ctr'].includes(baseMetric)) {
      return `${(num * 100).toFixed(1)}%`
    }

    return num.toLocaleString()
  }

  const chartConfig = yAxisKeys.reduce((config, key) => {
    const isContrast = key.endsWith('_contrast')
    const baseKey = isContrast ? key.replace('_contrast', '') : key
    const baseLabel = metricLabels[baseKey] || baseKey
    const label = isContrast ? `${baseLabel} (对比)` : baseLabel
    // 对比数据使用相同的颜色，但通过虚线区分
    const colorIndex = isContrast
      ? config[baseKey]?.colorIndex || Object.keys(config).length + 1
      : Object.keys(config).length + 1
    config[key] = {
      label,
      color: `hsl(var(--chart-${colorIndex}))`,
      colorIndex,
      isContrast,
    }
    return config
  }, {})

  const handleLegendClick = (payload) => {
    const dataKey = payload.dataKey
    if (hiddenKeys.includes(dataKey)) {
      setHiddenKeys(hiddenKeys.filter((key) => key !== dataKey))
    } else {
      setHiddenKeys([...hiddenKeys, dataKey])
    }
  }

  if (isLoading) {
    return (
      <div className="border rounded-md p-4">
        <h3 className="text-lg font-semibold mb-2">{title}</h3>
        <Skeleton className="h-[250px] w-full" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="border rounded-md p-4">
        <h3 className="text-lg font-semibold mb-2">{title}</h3>
        <div>Failed to load chart data.</div>
      </div>
    )
  }

  return (
    <div className="border rounded-lg p-4 flex flex-col flex-grow">
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            className="z-10"
            onClick={onToggleEnlarge}
          >
            {isEnlarged ? (
              <Minimize className="h-4 w-4" />
            ) : (
              <Maximize className="h-4 w-4" />
            )}
          </Button>
          <h3 className="font-bold">{title}</h3>
        </div>
        <div className="flex items-center space-x-2">
          <Select value={splitBy} onValueChange={setSplitBy}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Split by..." />
            </SelectTrigger>
            <SelectContent>
              {dimensionOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <ChartContainer
        config={chartConfig}
        className={cn('w-full', isEnlarged ? 'h-full' : 'min-h-[200px] flex-1')}
      >
        <ResponsiveContainer
          key={chartId}
          width="100%"
          height="100%"
          debounce={0}
        >
          <LineChart key={chartId} data={chartData}>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickFormatter={(value) => {
                const dateNum = parseInt(value.replace(/-/g, ''), 10)
                const [, weekDay] = getWeek(dateNum)
                return `${format(new Date(value), 'yyyy/MM/dd')} 周${weekDay}`
              }}
            />
            <YAxis
              yAxisId="left"
              tickFormatter={formatAxis}
              domain={[0, (dataMax) => dataMax * 1.2]}
            />
            <ChartTooltip
              cursor={false}
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  // 按基础键分组，将当前值和对比值放在一起
                  const grouped = {}
                  payload.forEach((pld) => {
                    const key = pld.dataKey
                    const isContrast = key.endsWith('_contrast')
                    const baseKey = isContrast ? key.replace('_contrast', '') : key
                    if (!grouped[baseKey]) {
                      grouped[baseKey] = { current: null, contrast: null }
                    }
                    if (isContrast) {
                      grouped[baseKey].contrast = pld
                    } else {
                      grouped[baseKey].current = pld
                    }
                  })

                  // 格式化日期显示周几
                  const labelDateNum = parseInt(label.replace(/-/g, ''), 10)
                  const [, labelWeekDay] = getWeek(labelDateNum)
                  const formattedLabel = `${format(
                    new Date(label),
                    'yyyy/MM/dd',
                  )} 周${labelWeekDay}`

                  // 计算对比日期
                  let contrastDateLabel = null
                  if (contrastEnabled && contrastType) {
                    const contrastDateNum = getContrastDate(
                      labelDateNum,
                      contrastType,
                      customYear,
                      customMonth,
                    )
                    if (contrastDateNum) {
                      const contrastDateStr = parseContrastDate(contrastDateNum)
                      const [, contrastWeekDay] = getWeek(contrastDateNum)
                      contrastDateLabel = `${format(
                        new Date(contrastDateStr),
                        'yyyy/MM/dd',
                      )} 周${contrastWeekDay}`
                    }
                  }

                  return (
                    <div className="bg-background border p-2 rounded-md shadow-md min-w-[250px]">
                      <div className="mb-2">
                        <p className="label font-semibold">{formattedLabel}</p>
                        {contrastDateLabel && (
                          <p className="label text-xs text-muted-foreground">
                            {contrastDateLabel}
                          </p>
                        )}
                      </div>
                      {Object.entries(grouped).map(([baseKey, { current, contrast }]) => {
                        if (!current && !contrast) return null
                        const currentValue = current?.value
                        const contrastValue = contrast?.value
                        const diff =
                          currentValue != null && contrastValue != null
                            ? currentValue - contrastValue
                            : null
                        const diffPercent =
                          contrastValue != null && contrastValue !== 0 && diff != null
                            ? (diff / contrastValue) * 100
                            : null

                        return (
                          <div key={baseKey} className="mb-2">
                            {current && (
                              <div className="intro flex justify-between items-center">
                                <div className="flex items-center">
                                  <div
                                    className="w-2 h-2 rounded-full mr-2"
                                    style={{ backgroundColor: current.color }}
                                  />
                                  <span>
                                    {chartConfig[current.dataKey]?.label || current.name}
                                  </span>
                                </div>
                                <span>{formatAxis(current.value)}</span>
                              </div>
                            )}
                            {contrast && (
                              <div className="intro flex justify-between items-center text-xs text-muted-foreground">
                                <div className="flex items-center">
                                  <div
                                    className="w-2 h-2 rounded-full mr-2"
                                    style={{
                                      backgroundColor: contrast.color,
                                      opacity: 0.6,
                                    }}
                                  />
                                  <span>
                                    {chartConfig[contrast.dataKey]?.label || contrast.name}
                                  </span>
                                </div>
                                <span>{formatAxis(contrast.value)}</span>
                              </div>
                            )}
                            {diff != null && (
                              <div className="text-xs">
                                <span
                                  className={
                                    diff >= 0 ? 'text-green-600' : 'text-red-600'
                                  }
                                >
                                  差异: {formatAxis(Math.abs(diff))}
                                  {diffPercent != null &&
                                    ` (${diffPercent >= 0 ? '+' : ''}${diffPercent.toFixed(1)}%)`}
                                </span>
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )
                }
                return null
              }}
            />
            <ChartLegend
              onClick={handleLegendClick}
              formatter={(value, entry) => {
                const isHidden = hiddenKeys.includes(entry.dataKey)
                return (
                  <span style={{ color: isHidden ? '#ccc' : 'inherit' }}>
                    {value}
                  </span>
                )
              }}
            />
            {yAxisKeys.map((key) => {
              const config = chartConfig[key]
              const isContrast = config?.isContrast || false
              return (
                <Line
                  key={key}
                  dataKey={key}
                  type="monotone"
                  stroke={config?.color || '#8884d8'}
                  strokeOpacity={hiddenKeys.includes(key) ? 0 : isContrast ? 0.6 : 1}
                  strokeWidth={2}
                  strokeDasharray={isContrast ? '5 5' : undefined}
                  dot={false}
                  yAxisId="left"
                  animationDuration={300}
                />
              )
            })}
          </LineChart>
        </ResponsiveContainer>
      </ChartContainer>
    </div>
  )
}

export default AdmobChart
